import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { useTheme } from 'components/core/theme-provider';
import { Checkbox } from 'components/ui/checkbox';
import type { TeamsMember, TeamsMemberRequest } from '../teams-mock-data';
import type { TeamsPeopleColumnVisibility } from './teams-view-columns-popover';
import {
  teamsMemberStatusActiveClassName,
  teamsMemberStatusInactiveClassName,
  teamsStatusBadgeClassName,
} from './teams-styles';

type Translate = (key: string, options?: { defaultValue?: string }) => string;

function requestLabel(request: TeamsMemberRequest, t: Translate) {
  if (request === 'upgrade') {
    return t('TEAMS_REQUEST_UPGRADE', { defaultValue: 'Upgrade request' });
  }
  if (request === 'credit') {
    return t('TEAMS_REQUEST_CREDIT', { defaultValue: 'Credit request' });
  }
  return null;
}

export function MemberNameCell({
  member,
  nameColor,
  mutedColor,
}: {
  member: TeamsMember;
  nameColor: string;
  mutedColor: string;
}) {
  const { t } = useTranslation();
  const badge = requestLabel(member.request, t);

  return (
    <div className="flex min-w-0 items-start gap-3">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-grey-100 text-xs font-medium leading-heading text-grey-800">
        {member.initials}
      </div>
      <div className="flex min-w-0 flex-col">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span title={member.name} className={cn('truncate text-sm font-medium leading-body', nameColor)}>
            {member.name}
          </span>
          {member.isYou && (
            <span className={cn('text-sm leading-body', mutedColor)}>
              {t('TEAMS_YOU', { defaultValue: '(you)' })}
            </span>
          )}
          {badge && (
            <span
              className={cn(
                'shrink-0 rounded-card px-2 py-0.5 text-xs font-medium leading-heading',
                'bg-warning-background text-warning-high-emphasis'
              )}
            >
              {badge}
            </span>
          )}
        </div>
        <span title={member.email} className={cn('truncate text-xs leading-heading', mutedColor)}>
          {member.email}
        </span>
      </div>
    </div>
  );
}

export function TeamsMemberStatusBadge({ isActive }: { isActive: boolean }) {
  const { t } = useTranslation();

  return (
    <span
      className={cn(
        teamsStatusBadgeClassName,
        'rounded-full px-2.5',
        isActive ? teamsMemberStatusActiveClassName : teamsMemberStatusInactiveClassName
      )}
    >
      {isActive
        ? t('TEAMS_STATUS_ACTIVE', { defaultValue: 'Active' })
        : t('TEAMS_STATUS_INACTIVE', { defaultValue: 'Inactive' })}
    </span>
  );
}

type TeamsPeopleCardProps = {
  member: TeamsMember;
  isSelected: boolean;
  onSelectedChange: (checked: boolean) => void;
  columnVisibility: TeamsPeopleColumnVisibility;
  /** The plan picker, built by the tab since it owns the seat state. */
  plan: ReactNode;
  creditAccess: ReactNode;
  actions: ReactNode;
};

/** Phone and tablet layout of one person: header row, plan picker, then a 2-column detail grid. */
export function TeamsPeopleCard({
  member,
  isSelected,
  onSelectedChange,
  columnVisibility,
  plan,
  creditAccess,
  actions,
}: TeamsPeopleCardProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const nameColor = theme === 'dark' ? 'text-true-white' : 'text-grey-950';
  const mutedColor = theme === 'dark' ? 'text-grey-400' : 'text-grey-500';
  const valueColor = theme === 'dark' ? 'text-grey-50' : 'text-grey-900';
  const borderColor = theme === 'dark' ? 'border-border' : 'border-grey-100';
  const hasDetails = columnVisibility.creditAccess || columnVisibility.lastLogin || columnVisibility.joinedOn;
  const labelClassName = cn('text-xs leading-heading', mutedColor);
  const dateClassName = cn('mt-1 text-sm leading-body', valueColor);

  return (
    <li
      className={cn(
        'flex flex-col gap-3 rounded-section border bg-true-white p-4 dark:bg-card',
        borderColor
      )}
    >
      <div className="flex items-start gap-3">
        {/* mt-2.5 centers the 16px box on the 36px avatar. */}
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelectedChange(checked === true)}
          aria-label={t('TEAMS_SELECT_MEMBER', {
            defaultValue: 'Select {{name}}',
            name: member.name,
          })}
          className="mt-2.5 shrink-0"
        />
        <div className="min-w-0 flex-1">
          <MemberNameCell member={member} nameColor={nameColor} mutedColor={mutedColor} />
        </div>
        {columnVisibility.status && (
          <div className="mt-2 shrink-0">
            <TeamsMemberStatusBadge isActive={member.status === 'active'} />
          </div>
        )}
        <div className="mt-1 shrink-0">{actions}</div>
      </div>

      {plan}

      {hasDetails && (
        // Credit access on the left, dates pushed to the right edge.
        <div className={cn('flex flex-wrap items-start justify-between gap-x-6 gap-y-3 border-t pt-3', borderColor)}>
          {columnVisibility.creditAccess && (
            <dl className="min-w-0">
              <dt className={labelClassName}>
                {t('TEAMS_COL_CREDIT_ACCESS', { defaultValue: 'Credit access' })}
              </dt>
              <dd className="mt-1">{creditAccess}</dd>
            </dl>
          )}
          <div className="ml-auto flex gap-6 text-right">
            {columnVisibility.lastLogin && (
              <dl>
                <dt className={labelClassName}>
                  {t('TEAMS_COL_LAST_LOGIN', { defaultValue: 'Last login' })}
                </dt>
                <dd className={dateClassName}>{member.lastLogin ?? '—'}</dd>
              </dl>
            )}
            {columnVisibility.joinedOn && (
              <dl>
                <dt className={labelClassName}>
                  {t('TEAMS_COL_JOINED_ON', { defaultValue: 'Joined on' })}
                </dt>
                <dd className={dateClassName}>{member.joinedOn ?? '—'}</dd>
              </dl>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
